/* Banking Partner Login (Phase PP-1 · Feb 2026).
   Dedicated entry point for banking partners. Uses the same backend
   `/api/auth/login` but redirects role=banking_partner straight to
   `/banking` and bounces every other role away.
*/
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Banknote, ShieldCheck, Building2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

export default function BankingLogin() {
  const { login, logout } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u.role !== "banking_partner" && u.role !== "admin") {
        await logout();
        toast.error("This portal is for banking partners only.");
        return;
      }
      toast.success(`Welcome, ${u.name || u.email}`);
      nav("/banking", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="banking-login" className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-slate-900 to-sky-900 text-slate-100">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md bg-sky-500/20 grid place-items-center">
            <Banknote className="w-5 h-5 text-sky-300" />
          </div>
          <div>
            <div className="font-semibold">verqo · Banking</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Partner Portal</div>
          </div>
        </div>
        <div className="space-y-6">
          <h1 className="display text-4xl leading-tight tracking-tight">
            Real-time visibility<br/>into every rail you operate.
          </h1>
          <div className="space-y-3 text-sm text-slate-300">
            <div className="flex items-start gap-2.5"><ShieldCheck className="w-4 h-4 text-sky-300 mt-0.5" /> <span>Transactions, webhooks, API health and chargebacks — all scoped to your bank.</span></div>
            <div className="flex items-start gap-2.5"><Building2 className="w-4 h-4 text-sky-300 mt-0.5" /> <span>Isolated tenant model · zero cross-partner data leakage.</span></div>
          </div>
        </div>
        <div className="text-[11px] text-slate-400">© verqo · Partner portal</div>
      </div>

      <div className="flex flex-col items-center justify-center p-8 lg:p-14">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4">
          <div className="flex items-center gap-2 lg:hidden mb-3">
            <Banknote className="w-5 h-5 text-sky-600" />
            <div className="font-semibold">verqo Banking</div>
          </div>
          <div>
            <h2 className="display text-2xl tracking-tight">Sign in</h2>
            <p className="text-sm text-slate-600 mt-1">Use the email + temporary password your verqo contact sent you.</p>
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="banking-login-email" autoComplete="email" required />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} data-testid="banking-login-password" autoComplete="current-password" required />
          </div>
          <Button type="submit" className="w-full" disabled={busy} data-testid="banking-login-submit">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
          <div className="text-[11px] text-slate-500 text-center">
            Lost your password? Contact your verqo partnerships lead.
          </div>
        </form>
      </div>
    </div>
  );
}
